import React, { memo, useCallback } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Dropdown, Pagination, Spin, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';

import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import formatDocument from 'v2/components/Shared/Masks/document';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import whitelistService from 'v2/services/whitelist';

import { Icon } from '@eduzz/ui-antd-theme';

const List = () => {
  const { message, modal } = App.useApp();
  const [isLoading] = useObservable(() => whitelistService.isLoading(), []);
  const [whitelists] = useObservable(() => whitelistService.getWhiteLists(), []);
  const [pagination] = useObservable(() => whitelistService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (_current: number, perPage: number) => {
      whitelistService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      whitelistService.setPagination({ ...pagination, current_page: page });
    },
    [pagination]
  );

  const openDialog = useCallback((whitelist: any) => {
    whitelistService.setCurrentWhiteList(whitelist);
    whitelistService.setCreateAndEditDialog(true);
  }, []);

  const [deleteWhitelist] = useCallbackObservable(
    (whitelist: any) => {
      return whitelistService.delete(whitelist).pipe(
        tap(() => message.success('Cliente removido com sucesso.')),
        catchError(() => {
          message.error('Ocorreu um erro ao tentar remover cliente');
          return of(true);
        })
      );
    },
    [message]
  );

  const onDelete = useCallback(
    (whitelist: any) => {
      modal.confirm({
        content: (
          <div className='flex flex-col gap-2'>
            <Typography.Paragraph className='mb-0'>
              <Typography.Text strong>Nome:</Typography.Text> {whitelist?.customer_name}
            </Typography.Paragraph>
            <Typography.Paragraph className='mb-0'>
              <Typography.Text strong>E-mail:</Typography.Text> {whitelist.customer_email}
            </Typography.Paragraph>
            <Typography.Paragraph className='mb-0'>
              <Typography.Text strong>CPF/CNPJ:</Typography.Text> {formatDocument.apply(whitelist.customer_document)}
            </Typography.Paragraph>
          </div>
        ),
        okText: 'Excluir',
        cancelText: 'Cancelar',
        title: 'Realmente deseja excluir da whitelist o cliente abaixo?',
        cancelButtonProps: { type: 'text' },
        okButtonProps: { danger: true },
        onOk: () => deleteWhitelist(whitelist)
      });
    },
    [deleteWhitelist, modal]
  );

  const editPermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:editar');
  const deletePermission = eduzzAccountsService.hasPermission('myeduzz:whitelist:excluir');

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id: number) => <Typography.Text type='secondary'>{id}</Typography.Text>
    },
    {
      title: 'Nome',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 200,
      render: (name: string) => <Typography.Text>{name}</Typography.Text>
    },
    {
      title: 'E-mail',
      dataIndex: 'customer_email',
      key: 'customer_email',
      width: 250,
      render: (email: string) => <Typography.Text>{email}</Typography.Text>
    },
    {
      title: 'CPF/CNPJ',
      dataIndex: 'customer_document',
      key: 'customer_document',
      width: 200,
      render: (doc: string) => formatDocument.apply(doc)
    },
    ...(editPermission || deletePermission
      ? [
          {
            title: 'Ações',
            key: 'actions',
            width: 80,
            fixed: 'right' as const,
            align: 'center' as const,
            render: (_: any, record: any) => {
              const items: MenuProps['items'] = [];

              if (editPermission) {
                items.push({ key: 'edit', label: 'Editar', onClick: () => openDialog(record) });
              }

              if (deletePermission) {
                items.push({ key: 'delete', label: 'Excluir', danger: true, onClick: () => onDelete(record) });
              }

              return (
                <Dropdown menu={{ items }} placement='bottomRight' trigger={['click']}>
                  <Button type='text' shape='circle' icon={<Icon.Ellipsis />} aria-label='Ações' />
                </Dropdown>
              );
            }
          }
        ]
      : [])
  ];

  return (
    <Spin spinning={!!isLoading}>
      <Table
        id='whitelist_table'
        size='small'
        sticky
        className='[&_.ant-table-tbody_.ant-typography]:text-sm'
        dataSource={whitelists || []}
        columns={columns}
        rowKey='id'
        scroll={{ x: 'max-content' }}
        pagination={false}
      />
      {!isLoading && pagination && (
        <div className='mt-4 flex justify-end'>
          <Pagination
            current={pagination.current_page}
            pageSize={pagination.per_page}
            total={pagination.total_items || 0}
            onChange={onChangePage}
            onShowSizeChange={onChangePerPage}
            showSizeChanger
            pageSizeOptions={[10, 20, 50]}
            showTotal={(total, range) => `${range[0]}-${range[1]} de ${total}`}
          />
        </div>
      )}
    </Spin>
  );
};

export default memo(List);
